import { clone } from "ramda";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LoadingSpinner } from "widgets";

import { type Tool } from "@/graphql.ts";
import { type IIdentifiable } from "@/redux/modules/table-data.ts";
import { checkInternet, loadAllTools } from "@/redux/modules/tools-admin.ts";
import { type IAppState } from "@/redux/reducer.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";
import { CELL_TYPE } from "@/settings/settings.js";
import { FileScannerCheckDialog } from "@/ui-modules/admin/FileScannerCheckDialog.tsx";

import { AsyncSearchWithExprParser } from "../core/forms/AsyncSearchWithExprParser.js";
import InMemoryTableWithSelectionModel from "../core/grid/InMemoryTableWithSelectionModel.js";
import PageHeader from "../core/navigation-panel/PageHeader.js";
import styleLayout from "../core/styles/PageLayout.module.scss";
import NCBIAgentWizard from "../playbooks/ncbi/NCBIAgentWizard.js";

export interface IToolsAdminContext {
  hasInternetConnection: boolean;
}

const adminToolsColumnDefs: { name: string; field: string; cellType: string }[] = [
  {
    name: "Name",
    field: "tool.name",
    cellType: CELL_TYPE.STRING,
  },
  {
    name: "Description",
    field: "tool.description",
    cellType: CELL_TYPE.STRING,
  },
];

/**
 * Tool Admin Page main component
 */
export const AdminToolsPage = () => {
  const dispatch = useDispatch<ReduxDispatch>();

  const tools = useSelector((state: IAppState) => state.toolsAdmin.tools);
  const isLoadingSessions = useSelector(
    (state: IAppState) => state.toolsAdmin.isLoadingTools || state.toolsAdmin.isCheckingInternet,
  );
  const hasInternetConnection = useSelector((state: IAppState) => state.toolsAdmin.hasInternetConnection);

  const [rowData, setRowData] = useState<IIdentifiable[]>([]);
  const [showConfigureDialog, setShowConfigureDialog] = useState(false);
  const [activeToolName, setActiveToolName] = useState<string | undefined>(undefined);

  const toolPropContainsTerm = useCallback((tool: Tool, terms: string[]) => {
    if (terms.length === 0) {
      return true;
    }
    const fieldValues = [tool.name, tool.description].map((v) => v?.toLowerCase() ?? "");
    const lowerTerms = terms.map((t) => t.toLowerCase());

    // Every term must be found in at least one field. The more terms the more precise the search
    return lowerTerms.every((term) => fieldValues.some((value) => value.includes(term)));
  }, []);

  const createRowData = useCallback(
    (terms: string[], tools: Tool[]): { id: number; tool: Tool }[] => {
      return tools
        .filter((tool: Tool) => toolPropContainsTerm(tool, terms))
        .map((tool: Tool) => {
          return {
            id: tool.id as number,
            tool: clone(tool), // we have to clone the tool because of a bug in ag-grid 13 (AG-771)
          };
        });
    },
    [toolPropContainsTerm],
  );

  useEffect(() => {
    const loadTools = () => {
      dispatch(checkInternet(null)).then((_hasInternet) => {
        dispatch(loadAllTools()).then((tools: Tool[]) => {
          setRowData(createRowData([], tools));
        });
      });
    };

    loadTools();
  }, [createRowData, dispatch]);

  const searchExprChanged = (_expr: string, terms: string[]) => {
    return new Promise<void>((resolve) => {
      const newRowData = createRowData(terms, tools);
      setRowData(newRowData);
      resolve();
    });
  };

  const handleDialogClose = () => {
    setShowConfigureDialog(false);
    setActiveToolName(undefined);
  };

  const handleNameClick = (tool: Tool) => {
    setShowConfigureDialog(true);
    setActiveToolName(tool.name ?? undefined);
  };

  const cellRendererParams = useMemo(
    () => ({
      "tool.name": {
        handleToolClicked: handleNameClick,
      },
    }),
    [],
  );

  const adminContext: IToolsAdminContext = useMemo(() => ({ hasInternetConnection }), [hasInternetConnection]);

  return (
    <div className={styleLayout.page} style={{ backgroundColor: "white" }}>
      <PageHeader
        title={"Admin Tools"}
        toolsWidgets={[
          <AsyncSearchWithExprParser onSearchExprChange={searchExprChanged} key={"admin-tools-page-widget-1"} />,
        ]}
      />
      {isLoadingSessions ? (
        <LoadingSpinner inCenter />
      ) : (
        <div style={{ height: "calc(100% - 50px)" }}>
          <InMemoryTableWithSelectionModel
            columns={adminToolsColumnDefs}
            rowGridData={rowData}
            useSelectionModel={false}
            uniqueGridIdentifier={"admin-tools"}
            cellRendererParamsLookup={cellRendererParams}
            gridContext={{ adminContext }}
          />
        </div>
      )}
      {/* This logic should be improved and consolidated once there will be more admin tools */}
      {showConfigureDialog && activeToolName != undefined && activeToolName.toLowerCase().includes("ncbi") && (
        <NCBIAgentWizard
          onClose={() => {
            setShowConfigureDialog(false);
            setActiveToolName(undefined);
          }}
          documentationUrl={undefined}
        />
      )}
      <FileScannerCheckDialog
        isOpen={
          showConfigureDialog &&
          activeToolName != undefined &&
          activeToolName.toLowerCase().includes("file system check")
        }
        onClose={handleDialogClose}
      />
    </div>
  );
};
